import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useConstants } from "../../Main/useConstants";
import { EGButton } from "../../../../../MainComponents/Button/EGButton/EGButton";
import { ChipParticipant } from "../../components";
import { SectionedSelect } from "../../../../../MainComponents/Select/SectionedSelect";
import { EGBasicSelect } from "../../../../../MainComponents/Select/EGBasicSelect";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { EGIconButton } from "../../../../../MainComponents/Button/IconButton/IconButton";

/** Modal for managing interview participants */
export const ParticipantManager = ({
  participants,
  setParticipants,
  participantsSections,
  interviewerId,
  isLoading,
}) => {
  const [t] = useTranslation("investigation");
  const [openDialog, setOpenDialog] = useState(false);
  const [participant, setParticipant] = useState({ id: "", from: "" });
  const [role, setRole] = useState("");

  const {
    interviews: { rolesOpts },
    functions: { getUserLabel },
  } = useConstants();

  const getAvailableSections = (sections = [], excludedIds = []) => {
    return sections.map((section) => ({
      ...section,
      options: (section.options || []).filter(
        (opt) => !excludedIds.includes(opt.value)
      ),
    }));
  };

  const handleAddParticipant = () => {
    setParticipants((prev) => [...prev, { ...participant, role }]);
    setParticipant({ id: "", from: "" });
    setRole("");
    setOpenDialog(false);
  };

  const handleDeleteParticipant = (idx) => {
    setParticipants((prev) => prev.filter((_, i) => i !== idx));
  };

  // IDs already assigned (participants) + interviewer if applicable
  const excludedIds = [
    ...participants.map((p) => p.id).filter(Boolean),
    ...(interviewerId ? [interviewerId] : []),
  ];

  // Filtered sections showing only the non-excluded ones
  const availableSections = getAvailableSections(
    participantsSections,
    excludedIds
  );

  // If there are no options in any section -> there are no participants left to assign
  const noMoreParticipants = availableSections.every(
    (s) => (s.options || []).length === 0
  );

  return (
    <Box mt={2}>
      {/* Dialog to add participants -> use availableSections */}
      <Dialog open={openDialog} maxWidth="xs" fullWidth>
        <EGIconButton
          iconName="cancel_circle"
          iconColor="primary"
          sx={{ position: "absolute", top: 10, right: 10 }}
          onClick={() => setOpenDialog(false)}
        />
        <DialogTitle>
          <Typography
            variant="h5"
            color="secondary"
            component="span"
            fontWeight={600}
          >
            {t("Interview.select_a_participant")}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            <SectionedSelect
              label={`${t("Interview.participant")}: `}
              value={participant.id}
              onChange={(opt) =>
                setParticipant({ id: opt.value, from: opt.section })
              }
              sections={availableSections}
              showHelpText={false}
            />
            <EGBasicSelect
              label={`${t("Interview.role")}: `}
              value={role}
              onChange={(e) => setRole(e.target.value)}
              options={rolesOpts}
              showHelpText={false}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <EGButton
            label={t("Interview.cancel")}
            onClick={() => setOpenDialog(false)}
          />
          <EGButton
            label={t("Interview.add")}
            onClick={handleAddParticipant}
            color={!participant.id || !role ? "" : "success"}
            disabled={!participant.id || !role}
            labelColor={!participant.id || !role ? "" : "white"}
          />
        </DialogActions>
      </Dialog>

      {/* Participants */}
      <Stack
        spacing={1}
        sx={{
          alignItems: "stretch",
          justifyContent: "start",
          flexFlow: "row wrap",
        }}
      >
        <Tooltip
          title={
            noMoreParticipants
              ? t(
                  "Interview.all_available_participants_have_already_been_assigned"
                )
              : ""
          }
        >
          <span>
            <EGButton
              onClick={() => setOpenDialog(true)}
              label={t("Interview.add_participant")}
              iconPosition="end"
              iconName="add_square"
              iconColor="teal"
              iconColorHover="teal"
              labelColor="teal"
              disabled={noMoreParticipants}
              loading={isLoading}
            />
          </span>
        </Tooltip>

        {participants.length > 0 && (
          <Typography variant="h6" color="secondary" my="auto" ml={1}>
            {`${t("Interview.participant")}${
              participants.length > 1 ? "s" : ""
            }: `}
          </Typography>
        )}

        {/* Participants chips list */}
        {participants.map((p, idx) => (
          <ChipParticipant
            key={idx}
            idx={idx}
            role={rolesOpts.find((r) => r.value === p.role)?.label || ""}
            name={getUserLabel(p)}
            handleDeleteParticipant={() => handleDeleteParticipant(idx)}
          />
        ))}
      </Stack>
    </Box>
  );
};
